import {
  Component, ElementRef, Input, TemplateRef,
  ViewChild, OnChanges, Output, SimpleChanges,
  OnDestroy, AfterViewInit, EventEmitter
} from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { messageService } from '../../message.service';
import { DocViewerConfig } from 'src/app/support/models/doc-viewer-config.model';
import { WebviewerPrintComponent } from '../../webviewer-print/webviewer-print.component';
import { Subject, takeUntil } from 'rxjs';
import { AppService } from 'src/app/app.service';
import { SessionTimeoutService } from 'src/app/session-timeout-dialog/session-timeout.service';
import { OneQmsPrinterService, TrayPrinter } from 'src/app/qms-printer-service';

@Component({
  selector: 'app-document-viewer-printer',
  templateUrl: './document-viewer-printer.component.html',
  styleUrls: ['./document-viewer-printer.component.scss']
})
export class DocumentViewerPrinterComponent
  implements OnChanges, AfterViewInit, OnDestroy {

  constructor(
    public dialog: MatDialog,
    private snackBar: MatSnackBar,
    public message: messageService,
    private elem: ElementRef,
    private appService: AppService,
    private sessionTimeoutService: SessionTimeoutService,
    private printerService: OneQmsPrinterService
  ) { }

  @Input() docViewer!: DocViewerConfig;
  @Output() closePopupsAfterPrint = new EventEmitter<void>();

  private hasOpened = false;
  private destroy$ = new Subject<void>();
  isProcessing = true;
  documentLoaded = false;
  isPrintButtonDisabled = false;

  @ViewChild('viewPopup2',    { static: true }) viewPopup2!: TemplateRef<any>;
  @ViewChild('printerPopup',  { static: true }) printerPopup!: TemplateRef<any>;
  @ViewChild('webViewerComp', { static: false }) webViewerComp!: WebviewerPrintComponent;

  visibleViewPopup!: MatDialogRef<TemplateRef<any>> | null;
  printerPopupRef!: MatDialogRef<any>;

  selectedPrinter: TrayPrinter | null = null;
  printers: TrayPrinter[] = [];

  pageSelection: 'ALL' | 'CUSTOM' = 'ALL';
  customPages: string = '';
  copies: number = 1;
  isDuplexEnabled: boolean = false;
  orientation: 'PORTRAIT' | 'LANDSCAPE' = 'PORTRAIT';

  viewPopup2Config = {
    width: '100%',
    height: '90%',
    panelClass: 'my-dialog',
    hasBackdrop: true,
    disableClose: true,
    position: { top: '10px' }
  };

  get displayName(): string {
    return this.docViewer?.docName?.length > 75
      ? this.docViewer.docName.substring(0, 75) + '...'
      : this.docViewer?.docName;
  }

  // ── Privilege check ────────────────────────────────────────────────────
  /**
   * Returns true if the logged-in user has BOTH:
   *   1. Print user privilege   (isDocumentDownloader = true)
   *   2. Print administrator privilege (isPrintAdministrator = true)
   *
   * If only print user → native Windows print popup is used.
   * If both → OneQMS Print Tray is used.
   *
   * isPrintAdministrator must be set in messageService from the user
   * role/permission response — same pattern as isDocumentDownloader.
   */
  get hasPrintTrayAccess(): boolean {
    // message.isPrintAdministrator — add this property to messageService
    // Set it when user roles are loaded, same as isDocumentDownloader
    return (this.message as any).isPrintAdministrator === true;
  }

  // ── Lifecycle ─────────────────────────────────────────────────────────

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['docViewer'] && this.docViewer && !this.hasOpened) {
      Promise.resolve().then(() => this.openPopUp());
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.visibleViewPopup?.close();
    this.printerPopupRef?.close();
  }

  ngAfterViewInit(): void { }

  close(): void { this.visibleViewPopup?.close(); }

  openPopUp(): void {
    if (this.hasOpened) return;
    this.hasOpened = true;

    this.visibleViewPopup = this.dialog.open(
      this.viewPopup2, this.viewPopup2Config);

    this.visibleViewPopup.afterOpened().subscribe(() => {
      setTimeout(() => {
        if (this.webViewerComp) {
          this.webViewerComp.documentLoaded
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => { this.documentLoaded = true; });
        }
      });
    });

    this.visibleViewPopup.afterClosed().subscribe(() => {
      this.hasOpened            = false;
      this.documentLoaded       = false;
      this.isPrintButtonDisabled = false;
    });

    setTimeout(() => (this.isProcessing = false), 0);
  }

  downloadDocument(): void {
    const formattedDate = new Intl.DateTimeFormat('en-US', {
      day: '2-digit', month: 'short', year: 'numeric'
    }).format(new Date());
    const fileName = `OneQmsDocs_${formattedDate}_`
      + `${this.docViewer.docName}.${this.docViewer.docExtension}`;
    const downloadURL = window.URL.createObjectURL(this.docViewer.blob);
    const link = document.createElement('a');
    link.href = downloadURL;
    link.download = fileName;
    link.click();
    window.URL.revokeObjectURL(downloadURL);
  }

  cancelViewPopup(): void {
    this.visibleViewPopup?.close();
    this.hasOpened = false;
  }

  // ── Print entry point — branches by privilege ─────────────────────────
  printDocument(): void {
    if (this.isPrintButtonDisabled) return;

    if (!this.message.isDocumentDownloader) {
      this.snackBar.open('Printing is not permitted.', 'Close',
        { duration: 3000 });
      return;
    }

    if (!this.documentLoaded) {
      this.snackBar.open('Document not loaded yet. Please wait.', 'Close',
        { duration: 2000 });
      return;
    }

    if (this.hasPrintTrayAccess) {
      // ── Print Administrator + Print User → OneQMS Print Tray ──────────
      console.log('[Print] User has Print Administrator privilege → Tray mode');
      this.openPrinterPopup();
    } else {
      // ── Print User only → Native Windows print popup ──────────────────
      console.log('[Print] Print User privilege only → Native print mode');
      this.triggerNativePrint();
    }
  }

  // ── Native Windows print ───────────────────────────────────────────────
  /**
   * Opens the browser/OS native print dialog.
   * Used when user has Print User privilege but NOT Print Administrator.
   *
   * Strategy:
   * 1. If WebViewerPrintComponent exposes a print() method → use it
   *    (best UX — prints directly from the viewer)
   * 2. Fallback → create hidden iframe with PDF blob → window.print()
   */
  triggerNativePrint(): void {
    // Option 1 — delegate to the web viewer component if it has print()
    if (this.webViewerComp && typeof (this.webViewerComp as any).print === 'function') {
      console.log('[Print] Native → calling webViewerComp.print()');
      (this.webViewerComp as any).print();
      return;
    }

    // Option 2 — blob-based iframe print (universal fallback)
    const blob = this.docViewer?.blob;
    if (!blob) {
      this.snackBar.open('Document not ready for printing.', 'Close',
        { duration: 3000 });
      return;
    }

    console.log('[Print] Native → iframe blob print');
    const url    = window.URL.createObjectURL(blob);
    const iframe = document.createElement('iframe');
    iframe.style.position  = 'fixed';
    iframe.style.top       = '-9999px';
    iframe.style.left      = '-9999px';
    iframe.style.width     = '0px';
    iframe.style.height    = '0px';
    iframe.style.border    = 'none';
    iframe.src = url;

    document.body.appendChild(iframe);

    iframe.onload = () => {
      // Small delay ensures PDF renders before print dialog opens
      setTimeout(() => {
        try {
          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();
        } catch (e) {
          console.error('[Print] iframe print failed:', e);
          // Last resort — open in new tab so user can Ctrl+P
          window.open(url, '_blank');
        }
        // Cleanup after dialog (print dialogs are synchronous — safe after delay)
        setTimeout(() => {
          document.body.removeChild(iframe);
          window.URL.revokeObjectURL(url);
        }, 2000);
      }, 500);
    };

    iframe.onerror = () => {
      console.error('[Print] iframe load error — opening in new tab');
      document.body.removeChild(iframe);
      window.URL.revokeObjectURL(url);
      window.open(url, '_blank');
    };
  }

  // ── Tray print flow ────────────────────────────────────────────────────

  async openPrinterPopup(): Promise<void> {
    this.isProcessing = true;

    const trayRunning = await this.printerService.isTrayRunning();
    if (!trayRunning) {
      this.isProcessing = false;
      this.snackBar.open(
        'Print Tray not reachable. Open https://oneqms-printer/api/health '
        + 'in Chrome, accept the certificate, then try again.',
        'Open Health Check',
        { duration: 10000 }
      ).onAction().subscribe(() => {
        window.open('https://oneqms-printer/api/health', '_blank');
      });
      return;
    }

    try {
      this.printers = await this.printerService.getPrinters();

      if (this.printers.length === 0) {
        this.isProcessing = false;
        this.snackBar.open(
          'No approved printers found. Contact administrator.',
          'Close', { duration: 5000 });
        return;
      }

      this.selectedPrinter = this.printers[0];
      this.isProcessing    = false;

      this.printerPopupRef = this.dialog.open(this.printerPopup, {
        width: '420px',
        disableClose: true
      });

    } catch (error: any) {
      this.isProcessing = false;
      console.error('[Print] openPrinterPopup error:', error);
      const msg = error?.message || 'Unknown error';
      this.snackBar.open(
        msg.includes('oneqms-printer') ? msg
          : 'Could not connect to printer tray. Is it running?',
        'Close', { duration: 8000 });
    }
  }

  async confirmPrint(): Promise<void> {
    if (!this.selectedPrinter) {
      this.snackBar.open('Please select a printer.', 'Close',
        { duration: 2000 });
      return;
    }

    const requestedCopies = Math.max(
      1, Math.min(this.docViewer.printPayloadData?.noOfCopy || 1, 20));

    const printRequest = {
      ...this.docViewer.printPayloadData,
      isPrintOrDownload: 'Print',
      printerName: this.selectedPrinter.printerName,
      pages: this.pageSelection === 'ALL' ? 'ALL' : this.customPages,
      fileName: this.docViewer.docName,
      noOfCopy: requestedCopies
    };

    const documentName = this.docViewer.docName ?? 'Document';
    this.isProcessing  = true;

    this.appService
      .downloadDocumentsDmsForPrinterPreview(printRequest, documentName)
      .subscribe({
        next: async (response: any) => {
          try {
            if (!response.body || response.body.byteLength === 0) {
              this.isProcessing = false;
              this.snackBar.open('Document download failed — empty response.',
                'Close', { duration: 3000 });
              return;
            }

            // PDF magic bytes check (%PDF)
            const bytes = new Uint8Array(response.body);
            const isPdf = bytes[0] === 0x25 && bytes[1] === 0x50
              && bytes[2] === 0x44 && bytes[3] === 0x46;

            if (!isPdf) {
              const text = new TextDecoder().decode(bytes.slice(0, 200));
              console.error('[Print] Non-PDF response:', text);
              this.isProcessing = false;
              this.snackBar.open('Server returned invalid document format.',
                'Close', { duration: 4000 });
              return;
            }

            const base64Pdf = await this.arrayBufferToBase64Safe(response.body);

            const moduleName = this.docViewer.printPayloadData?.subModule
              || this.docViewer.printPayloadData?.moduleName
              || 'DMS';

            await this.printerService.printPdf(
              this.selectedPrinter!,
              base64Pdf,
              requestedCopies,
              {
                fileName:      this.docViewer.docName,
                documentRefId: this.docViewer.printPayloadData?.documentId ?? '',
                moduleName,
                userId:        this.message.getUserId().toString() ?? '',
                orgId:         this.message.getOrgId().toString() ?? '',
                duplex:        this.isDuplexEnabled,
                orientation:   this.orientation,
              }
            );

            this.isProcessing = false;
            this.printerPopupRef?.close();
            this.visibleViewPopup?.close();
            this.closePopupsAfterPrint.emit();
            this.isPrintButtonDisabled = true;
            this.snackBar.open('✅ Document sent to printer!', 'Close',
              { duration: 3000 });

          } catch (error: any) {
            console.error('[Print] confirmPrint error:', error);
            this.isProcessing = false;
            const msg = error?.message || '';
            this.snackBar.open(
              msg.includes('oneqms-printer') ? msg
                : 'Printing failed. Check Print Tray is running.',
              'Close', { duration: 5000 });
          }
        },
        error: (httpError: any) => {
          console.error('[Print] Download failed:', httpError);
          this.isProcessing = false;
          this.snackBar.open(
            'Failed to download document: '
            + (httpError?.error?.error || httpError?.message || 'Unknown error'),
            'Close', { duration: 4000 });
        }
      });
  }

  /**
   * Converts ArrayBuffer to base64 safely using Blob + FileReader.
   * Avoids call stack overflow on large PDFs.
   */
  private arrayBufferToBase64Safe(buffer: ArrayBuffer): Promise<string> {
    return new Promise((resolve, reject) => {
      const blob   = new Blob([buffer], { type: 'application/pdf' });
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        const base64 = result.split(',')[1];
        if (base64) resolve(base64);
        else reject(new Error('FileReader returned empty base64'));
      };
      reader.onerror = () => reject(
        new Error('FileReader failed: ' + reader.error));
      reader.readAsDataURL(blob);
    });
  }

  private getCookie(name: string): string {
    const match = document.cookie.match(
      new RegExp('(^| )' + name + '=([^;]+)'));
    return match ? match[2] : '';
  }

  closePrinterPopup(): void { this.printerPopupRef?.close(); }

  onUserActivity(): void { this.sessionTimeoutService.notifyActivity(); }

  async refreshPrinterList(): Promise<void> {
    this.isProcessing = true;
    try {
      this.printers = await this.printerService.refreshPrinters();
      if (this.printers.length === 0) {
        this.snackBar.open(
          'No printers found for this machine. '
          + 'Check printer IP is registered in OneQMS.',
          'Close', { duration: 5000 });
      } else {
        this.selectedPrinter = this.printers[0];
        this.snackBar.open(`✅ ${this.printers.length} printer(s) loaded.`,
          'Close', { duration: 2000 });
      }
    } catch (err: any) {
      this.snackBar.open(
        'Refresh failed: ' + (err?.message || 'Unknown error'),
        'Close', { duration: 4000 });
    } finally {
      this.isProcessing = false;
    }
  }
}